import { gql } from '@apollo/client';
import { Alert, Badge } from '@apollo/orbit';
import * as Sentry from '@sentry/react';
import { compare } from 'compare-versions';
import React from 'react';

import { RelativeTimestamp } from 'src/components/timestamp/Timestamp';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { Timestamp } from 'src/lib/graphqlTypes/customScalarTypes';
import {
  BuildPipelineTrack,
  BuildPipelineTrackBadge,
} from 'src/lib/graphqlTypes/types';

import { BuildPipelineBadge } from './BuildPipelineBadge';
import { BuildPipelineTrackBanner } from './BuildPipelineTrackBanner';

export const FederationVersionInfoFragment = gql`
  fragment FederationVersionInfoFragment on GraphVariant {
    id
    latestLaunch {
      id
      buildInput {
        ... on CompositionBuildInput {
          version
        }
      }
    }
  }
`;

export const getMatchingAllowedTrack = (
  allowedTracks: GraphQLTypes.BuildPipelineTrackDetails[],
  buildPipelineTrack: string | undefined,
) =>
  allowedTracks.find(
    (track) => track.buildPipelineTrack === buildPipelineTrack,
  );

export const newMinorVersionAvailable = (
  allowedTracks: Array<GraphQLTypes.BuildPipelineTrackDetails>,
  compositionVersion: string,
) => {
  // If a variant is on `FED_NEXT` the GraphVariant.compositionVersion
  // wont be semver compatible. Exit early otherwise compare() will error below.
  if (compositionVersion === BuildPipelineTrack.FED_NEXT) {
    return false;
  }
  return allowedTracks.some((track) => {
    if (!track) return false;
    // when comparing versions, we only care about minor because that's
    // what the BE will return for composition version.
    const versionMinusPatch = track.compositionVersion.slice(
      0,
      track.compositionVersion.lastIndexOf('.'),
    );
    try {
      return (
        compare(versionMinusPatch, compositionVersion, '>') &&
        // don't show user "update available" when the next version is experimental
        track.badge !== BuildPipelineTrackBadge.EXPERIMENTAL
      );
    } catch (e) {
      Sentry.captureException(e, {
        level: 'error',
        extra: {
          error: `Incompatible semver composition version ${compositionVersion} computing newMinorVersionAvailable: `,
        },
      });
      return false;
    }
  });
};

export const FederationVersionInfo = ({
  compositionVersion,
  allowedTracks,
  buildPipelineTrack,
  versionUpdatedAt,
  latestLaunchCompositionVersion,
  isUsingFedNext,
}: {
  compositionVersion: string;
  latestLaunchCompositionVersion: string;
  allowedTracks: GraphQLTypes.BuildPipelineTrackDetails[];
  buildPipelineTrack: GraphQLTypes.BuildPipelineTrack;
  versionUpdatedAt?: Timestamp['output'] | null;
  isUsingFedNext: boolean;
}) => {
  const matchingTrack = getMatchingAllowedTrack(
    allowedTracks,
    buildPipelineTrack,
  );

  const newVersionAvailable = newMinorVersionAvailable(
    allowedTracks,
    compositionVersion,
  );

  // We always want to tell the user that the version they're on is either deprecated
  // or unsupported even if there's an update available
  const showNewVersionBadgeAndBanner =
    newVersionAvailable &&
    matchingTrack?.badge !== BuildPipelineTrackBadge.DEPRECATED &&
    matchingTrack?.badge !== BuildPipelineTrackBadge.UNSUPPORTED;

  const versionSplit = matchingTrack?.compositionVersion.split('.');
  let minorVersion = '';
  if (matchingTrack?.compositionVersion === '0.23.2') {
    minorVersion = 'v1.0';
  } else if (matchingTrack?.compositionVersion === '0.29.1') {
    minorVersion = 'v1.1';
  } else {
    minorVersion = versionSplit ? `v${versionSplit[0]}.${versionSplit[1]}` : '';
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center">
        <p className="mr-1">
          {isUsingFedNext ? latestLaunchCompositionVersion : minorVersion}
        </p>
        {showNewVersionBadgeAndBanner ? (
          <Badge variant="info" className="mr-2">
            UPDATE AVAILABLE
          </Badge>
        ) : (
          matchingTrack &&
          matchingTrack.badge && (
            <BuildPipelineBadge className="mr-2" status={matchingTrack.badge} />
          )
        )}
        {versionUpdatedAt && (
          <>
            <p className="mr-1">
              <span className="mr-1">•</span> last updated
            </p>
            <RelativeTimestamp
              className="font-semibold"
              value={versionUpdatedAt}
            />
          </>
        )}
      </div>
      {showNewVersionBadgeAndBanner ? (
        <Alert status="info" className="mt-4">
          <span className="font-semibold">
            A new Federation version is now available.
          </span>{' '}
          Upgrade now to leverage the latest features and supported directives.
        </Alert>
      ) : (
        matchingTrack &&
        matchingTrack.badge && (
          <BuildPipelineTrackBanner
            className="mt-4"
            status={matchingTrack.badge}
            track={buildPipelineTrack}
          />
        )
      )}
    </div>
  );
};
